import { z } from "zod";
import { supabase } from "./supabase";

const OrganizationAccessSchema = z.object({
  organization: z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    role: z.enum(["professional", "organization_admin", "content_editor", "support_admin"]),
  }),
  access: z.object({
    active: z.boolean(),
    plan: z.enum(["SOLO", "TEAM", "BUSINESS", "ORGANIZATION", "INTRO"]).nullable(),
    status: z.enum(["trialing", "active", "past_due", "canceled", "expired", "incomplete", "manual"]),
    provider: z.enum(["app_store", "play_store", "stripe", "manual", "revenuecat"]).nullable(),
    productId: z.string().nullable(),
    expiresAt: z.string().datetime({ offset: true }).nullable(),
    seatLimit: z.number().int().nonnegative(),
    unitLimit: z.number().int().positive().nullable(),
    statisticsLevel: z.enum(["basic", "full", "organization"]),
    cancelAtPeriodEnd: z.boolean(),
  }),
});

const AccessErrorSchema = z.object({
  error: z.enum(["not_authenticated", "account_not_activated", "origin_not_allowed", "internal_error"]),
});

export type OrganizationAccess = z.infer<typeof OrganizationAccessSchema>;

export class OrganizationAccessError extends Error {
  constructor(readonly code: "not_authenticated" | "account_not_activated" | "access_expired" | "unavailable") {
    super(code);
  }
}

export async function getOrganizationAccess(): Promise<OrganizationAccess> {
  if (!supabase) throw new OrganizationAccessError("unavailable");
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session) throw new OrganizationAccessError("not_authenticated");
  const apiBase = (import.meta.env.VITE_API_URL ?? "http://localhost:8787").replace(/\/$/, "");
  let response: Response;
  try {
    response = await fetch(`${apiBase}/billing/access`, {
      headers: { Authorization: `Bearer ${data.session.access_token}` },
    });
  } catch {
    throw new OrganizationAccessError("unavailable");
  }
  const raw = await response.json() as unknown;
  if (!response.ok) {
    const parsedError = AccessErrorSchema.safeParse(raw);
    if (parsedError.success && parsedError.data.error === "account_not_activated") throw new OrganizationAccessError("account_not_activated");
    if (response.status === 401) throw new OrganizationAccessError("not_authenticated");
    throw new OrganizationAccessError("unavailable");
  }
  const access = OrganizationAccessSchema.parse(raw);
  if (!access.access.active) throw new OrganizationAccessError("access_expired");
  return access;
}

export async function getOrganizationAccessIncludingInactive(): Promise<OrganizationAccess> {
  if (!supabase) throw new OrganizationAccessError("unavailable");
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session) throw new OrganizationAccessError("not_authenticated");
  const apiBase = (import.meta.env.VITE_API_URL ?? "http://localhost:8787").replace(/\/$/, "");
  const response = await fetch(`${apiBase}/billing/access`, { headers: { Authorization: `Bearer ${data.session.access_token}` } });
  const raw = await response.json() as unknown;
  if (!response.ok) {
    const parsedError = AccessErrorSchema.safeParse(raw);
    if (parsedError.success && parsedError.data.error === "account_not_activated") throw new OrganizationAccessError("account_not_activated");
    throw new OrganizationAccessError(response.status === 401 ? "not_authenticated" : "unavailable");
  }
  return OrganizationAccessSchema.parse(raw);
}

export function accessErrorMessage(error: unknown): string {
  if (!(error instanceof OrganizationAccessError)) return "Samklang kunde inte kontrollera åtkomsten just nu. Försök igen.";
  if (error.code === "not_authenticated") return "Logga in för att fortsätta.";
  if (error.code === "account_not_activated") return "Ditt konto är inte aktiverat ännu. Öppna länken i inbjudningsmejlet eller kontakta er administratör.";
  if (error.code === "access_expired") return "Din organisations tillgång till Samklang har upphört. En administratör kan förnya den.";
  return "Samklang kunde inte kontrollera organisationens åtkomst. Kontrollera anslutningen och försök igen.";
}
